import { z } from "zod";

/**
 * Supported locales for the application
 * Prioritized in the following order:
 * Arabic, English, Mandarin Chinese, Japanese, Russian, German, French, Spanish, Hindi, Turkish, Swedish
 */
export type LocaleType = "ar" | "en" | "zh" | "ja" | "ru" | "de" | "fr" | "es" | "hi" | "tr" | "sv";

export type ApiResponse = {
  message: {
    id: string;
    role: "assistant";
    content: string;
  };
};

export type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  avatarSrc?: string;
  framework?: string;
  isPartial?: boolean; // For streaming partial responses
  isLoading?: boolean; // For loading state
  model?: ModelType;   // Reference to the model that generated this message
  notice?: "truncated" | "refusal"; // System notice surfaced as a localized banner
};

export type ModelType = "claude" | "deepseek" | "mjlab";

export type ModelMessages = {
  [K in ModelType]: Message[];
};

export const messageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  timestamp: z.number(),
  avatarSrc: z.string().optional(),
  framework: z.string().optional(),
  isPartial: z.boolean().optional(), // For streaming partial responses
  isLoading: z.boolean().optional(), // For loading state
  model: z.enum(["claude", "deepseek", "mjlab"]).optional(), // Model reference
  notice: z.enum(["truncated", "refusal"]).optional() // System notice type
});

export const modelMessagesSchema = z.record(z.enum(["claude", "deepseek", "mjlab"]), z.array(messageSchema));

// Shared utility functions
export const isArabicText = (text: string): boolean => {
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F]/g;
  const matches = text.match(arabicPattern);
  if (!matches) return false;
  return matches.length > text.length * 0.3;
};

import i18next from "i18next";

export const createWelcomeMessage = (model: ModelType): Message => {
  // Get translated message parts using i18n, making sure "Mila" is not translated
  const helloTemplate = i18next.t("Hello, I am {{name}}, an AI assistant made by MJ.", {
    name: "Mila", // Using "Mila" directly to ensure it's never translated
    interpolation: { escapeValue: false }
  });
  const expert = i18next.t("I am expert in Ministry of Commerce laws and regulations for Saudi Arabia.");
  const ask = i18next.t("Ask me a question, and I will gladly answer you.");
  const confidential = i18next.t("Do not share confidential data.");
  
  return {
    id: "welcome",
    role: "assistant",
    content: `${helloTemplate} ${expert} ${ask} ${confidential}`,
    timestamp: Date.now(),
    avatarSrc: "/assets/icons/Mila-Avatar.png",
  };
};

export const createDisclaimerMessage = (): Message => {
  // Get translated disclaimer with "Mila" kept as an untranslated name
  const disclaimer = i18next.t("Disclaimer: {{name}} is an experimental AI assistant designed to respond using data from Saudi Ministry of Commerce laws and regulations, avoiding assumptions or inaccuracies. I am not responsible for its behavior or the correctness of its responses.", {
    name: "Mila", // Using "Mila" directly to ensure it's never translated
    defaultValue: "**Disclaimer:** Mila is an experimental AI assistant designed to respond using data from Saudi Ministry of Commerce laws and regulations, avoiding assumptions or inaccuracies. I am not responsible for its behavior or the correctness of its responses.",
    interpolation: { escapeValue: false }
  });

  return {
    id: "disclaimer",
    role: "assistant",
    content: disclaimer,
    timestamp: Date.now() + 1,
    avatarSrc: `/assets/icons/MJ-192x192.webp?v=${Date.now()}`, // Using the high-contrast MJ avatar specifically for disclaimers with cache busting
  };
};

export const errorMessages: Record<number, string> = {
  400: "Invalid request. Please check your input and try again.",
  401: "Authentication failed. Please log in again.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found.",
  429: "Rate limit reached. Please wait a moment before sending another message.",
  500: "Internal server error. Our team has been notified.",
  502: "The AI service is temporarily unavailable. Please try again later.",
  503: "Service is currently undergoing maintenance. Please try again shortly.",
};